// Services do cofre de certificados mTLS do merchant (superAdmin) — molde
// services/admin/processor-config.ts: queryOptions exportada p/ teste, gcTime 0
// (nunca exibir cache velho de material de segurança), invalidação por
// predicate e toasts com getErrorMessage.
//
// Contrato: payment/docs/MTLS_CERTIFICATE_VAULT.md.
import { api } from "@/api"
import { BASE_URL, ENDPOINTS } from "@/constants"
import { toast } from "@/hooks/use-toast"
import {
  getApiErrorMessage,
  hasApiErrorCode,
  hasStatusCode,
} from "@/lib/api-error"
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@/libs"
import type {
  MerchantMtlsCertificate,
  MerchantMtlsCertificateCreatePayload,
  MerchantMtlsCertificateHistoryItem,
  MerchantMtlsCertificateReplacePayload,
} from "@/types/admin-merchant-mtls-certificate"

export const ADMIN_MERCHANT_MTLS_CERTIFICATES_QUERY_KEY =
  "admin-merchant-mtls-certificates"

// Nenhuma URL leva o merchant: o gateway resolve pelo token. Mandar daqui
// deixaria o cliente escolher qual cofre abrir.
const listUrl = () =>
  `${BASE_URL}${ENDPOINTS.ADMIN_MERCHANT_MTLS_CERTIFICATES}`

const entryUrl = (certificateId: string) =>
  `${BASE_URL}${ENDPOINTS.ADMIN_MERCHANT_MTLS_CERTIFICATE.replace("{{certificateId}}", certificateId)}`

const historyUrl = (certificateId: string) =>
  `${BASE_URL}${ENDPOINTS.ADMIN_MERCHANT_MTLS_CERTIFICATE_HISTORY.replace("{{certificateId}}", certificateId)}`

const renewUrl = (certificateId: string) =>
  `${BASE_URL}${ENDPOINTS.ADMIN_MERCHANT_MTLS_CERTIFICATE_RENEW.replace("{{certificateId}}", certificateId)}`

export const merchantMtlsCertificatesQueryOptions = () => ({
  queryKey: [ADMIN_MERCHANT_MTLS_CERTIFICATES_QUERY_KEY],
  queryFn: async (): Promise<MerchantMtlsCertificate[]> => {
    const { data } =
      await api.get<MerchantMtlsCertificate[]>(listUrl())
    return data
  },
  // Material de segurança: cache velho aqui mostraria um certificado que já
  // foi substituído, e alguém decidiria não renovar por causa disso.
  gcTime: 0,
})

export const useMerchantMtlsCertificates = () =>
  useQuery(merchantMtlsCertificatesQueryOptions())

const invalidateVault = (
  queryClient: ReturnType<typeof useQueryClient>
) =>
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] ===
      ADMIN_MERCHANT_MTLS_CERTIFICATES_QUERY_KEY,
  })

/**
 * Uma entrada do cofre.
 *
 * Busca o detalhe, não a lista: só o detalhe traz `linkedConfigs` — QUAIS configurações dependem
 * do certificado. A lista manda apenas a contagem, para não pagar a varredura das tabelas de
 * provider por linha.
 */
export const merchantMtlsCertificateQueryOptions = (
  certificateId: string
) => ({
  queryKey: [
    ADMIN_MERCHANT_MTLS_CERTIFICATES_QUERY_KEY,
    certificateId,
  ],
  queryFn: async (): Promise<MerchantMtlsCertificate> => {
    const { data } = await api.get<MerchantMtlsCertificate>(
      entryUrl(certificateId)
    )
    return data
  },
  enabled: Boolean(certificateId),
  gcTime: 0,
})

export const useMerchantMtlsCertificate = (
  certificateId: string
) =>
  useQuery(
    merchantMtlsCertificateQueryOptions(certificateId)
  )

export const merchantMtlsCertificateHistoryQueryOptions = (
  certificateId: string
) => ({
  queryKey: [
    ADMIN_MERCHANT_MTLS_CERTIFICATES_QUERY_KEY,
    certificateId,
    "history",
  ],
  queryFn: async (): Promise<
    MerchantMtlsCertificateHistoryItem[]
  > => {
    const { data } = await api.get<{
      content?: MerchantMtlsCertificateHistoryItem[]
    }>(historyUrl(certificateId))
    return data?.content ?? []
  },
  enabled: Boolean(certificateId),
})

export const useMerchantMtlsCertificateHistory = (
  certificateId: string
) =>
  useQuery(
    merchantMtlsCertificateHistoryQueryOptions(
      certificateId
    )
  )

export const useCreateMerchantMtlsCertificateMutation =
  () => {
    const queryClient = useQueryClient()

    return useMutation<
      MerchantMtlsCertificate,
      unknown,
      MerchantMtlsCertificateCreatePayload
    >({
      mutationFn: async (payload) => {
        const { data } =
          await api.post<MerchantMtlsCertificate>(
            listUrl(),
            payload
          )
        return data
      },
      onSuccess: async () => {
        toast({
          title: "Certificado cadastrado",
          variant: "success",
        })
        await invalidateVault(queryClient)
      },
      onError: (error) => {
        // 409 = o merchant já tem este mesmo par guardado. Cadastrar de novo
        // traria de volta o "renove duas vezes" que o cofre elimina.
        //
        // A mensagem do backend NÃO é repassada: ele reusa a chave
        // PROCESSOR_CONFIG_CONFLICT, cujo texto padrão fala de configuração de
        // processor — mandaria o operador procurar problema no lugar errado.
        if (
          hasApiErrorCode(
            error,
            "PROCESSOR_CONFIG_CONFLICT"
          ) ||
          hasStatusCode(error, 409)
        ) {
          toast({
            title:
              "Este certificado já está cadastrado para o lojista.",
            variant: "destructive",
          })
          return
        }
        toast({
          title: getApiErrorMessage(
            error,
            "Erro ao cadastrar o certificado"
          ),
          variant: "destructive",
        })
      },
    })
  }

export const useReplaceMerchantMtlsCertificateMutation = (
  certificateId: string
) => {
  const queryClient = useQueryClient()

  return useMutation<
    MerchantMtlsCertificate,
    unknown,
    MerchantMtlsCertificateReplacePayload
  >({
    mutationFn: async (payload) => {
      const { data } =
        await api.put<MerchantMtlsCertificate>(
          entryUrl(certificateId),
          payload
        )
      return data
    },
    onSuccess: async (result) => {
      // O número importa: o operador precisa ver que uma ação atualizou N
      // configurações, não só a que ele estava olhando.
      toast({
        title: `Certificado substituído em ${result.linkedConfigsCount} configuração(ões)`,
        variant: "success",
      })
      await invalidateVault(queryClient)
    },
    onError: (error) => {
      toast({
        title: getApiErrorMessage(
          error,
          "Erro ao substituir o certificado"
        ),
        variant: "destructive",
      })
    },
  })
}

export const useDeleteMerchantMtlsCertificateMutation =
  () => {
    const queryClient = useQueryClient()

    return useMutation<void, unknown, string>({
      mutationFn: async (certificateId) => {
        await api.delete(entryUrl(certificateId))
      },
      onSuccess: async () => {
        toast({
          title: "Certificado removido",
          variant: "success",
        })
        await invalidateVault(queryClient)
      },
      onError: (error) => {
        // 409 = ainda há configuração usando. Apagar deixaria o método sem
        // material e ele pararia no próximo handshake.
        if (
          hasApiErrorCode(
            error,
            "PROCESSOR_CONFIG_CONFLICT"
          ) ||
          hasStatusCode(error, 409)
        ) {
          toast({
            title:
              "Certificado em uso — desvincule as configurações antes de remover.",
            variant: "destructive",
          })
          return
        }
        toast({
          title: getApiErrorMessage(
            error,
            "Erro ao remover o certificado"
          ),
          variant: "destructive",
        })
      },
    })
  }

/**
 * Renomeia a entrada — só o rótulo, o material fica intocado.
 *
 * O nome é o que identifica a entrada no combo da configuração; um rótulo genérico deixa de
 * servir assim que o merchant tem mais de um certificado.
 */
export const useRenameMerchantMtlsCertificateMutation = (
  certificateId: string
) => {
  const queryClient = useQueryClient()

  return useMutation<
    MerchantMtlsCertificate,
    unknown,
    string
  >({
    mutationFn: async (name) => {
      const { data } =
        await api.patch<MerchantMtlsCertificate>(
          entryUrl(certificateId),
          { name }
        )
      return data
    },
    onSuccess: async () => {
      toast({
        title: "Certificado renomeado",
        variant: "success",
      })
      await invalidateVault(queryClient)
    },
    onError: (error) => {
      toast({
        title: getApiErrorMessage(
          error,
          "Erro ao renomear o certificado"
        ),
        variant: "destructive",
      })
    },
  })
}

export const useRenewMerchantMtlsCertificateMutation =
  () => {
    const queryClient = useQueryClient()

    return useMutation<unknown, unknown, string>({
      mutationFn: async (certificateId) => {
        const { data } = await api.post(
          renewUrl(certificateId)
        )
        return data
      },
      onSuccess: async () => {
        toast({
          title: "Certificado renovado",
          variant: "success",
        })
        await invalidateVault(queryClient)
      },
      onError: (error) => {
        // 422 = flag desligada ou nenhum provider Itaú vinculado (só o Itaú tem
        // STS) — informativo, não é falha do operador.
        if (hasStatusCode(error, 422)) {
          toast({
            title:
              "Renovação automática indisponível para este certificado.",
          })
          return
        }
        // 400 = certificado compartilhado com outro banco; renovar sobrescreveria
        // o material que ele usa.
        toast({
          title: getApiErrorMessage(
            error,
            "Erro ao renovar o certificado"
          ),
          variant: "destructive",
        })
      },
    })
  }
